import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import {
    Table, Button, Icon, Label, Header, Loader, Message, Modal, Dropdown, Form, Input, List,
} from 'semantic-ui-react';

import {getAuthToken, getClientAud} from '../../selectors';
import {WITHDRAWAL_TYPE_OPTIONS} from '../../legalPages';

const EMPTY_FORM = {
    first_name: '',
    last_name: '',
    email: '',
    // A Widerruf that arrives by email or post often has no order number at all,
    // but the record requires one - so give the admin something meaningful to
    // overwrite rather than blocking them.
    order_id: 'manuell erfasst',
    withdrawal_type: '',
    note: '',
};

const STATUS_FILTERS = [
    {key: 'all', text: 'Alle', value: ''},
    {key: 'pending', text: 'Offen', value: 'pending'},
    {key: 'handled', text: 'Bearbeitet', value: 'handled'},
    {key: 'rejected', text: 'Abgelehnt', value: 'rejected'},
];

const STATUS_LABELS = {
    pending: {text: 'Offen', color: 'orange'},
    handled: {text: 'Bearbeitet', color: 'green'},
    rejected: {text: 'Abgelehnt', color: 'grey'},
};

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

const StatusLabel = ({status}) => {
    const spec = STATUS_LABELS[status] || {text: status, color: 'grey'};
    return <Label size="tiny" color={spec.color}>{spec.text}</Label>;
};

// Whole days since the Abo was taken out — to show whether a Widerruf is still
// within the statutory 14-day window.
function daysSince(value) {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
}

const AboDate = ({value}) => {
    if (!value) return null;
    const days = daysSince(value);
    const withinWindow = days !== null && days <= 14;
    return (
        <div style={{fontSize: 12, color: '#555', marginTop: 3}}>
            Abo abgeschlossen: {formatDate(value)}{' '}
            <Label size="tiny" color={withinWindow ? 'green' : 'grey'}>
                {withinWindow ? `im 14-Tage-Widerruf (Tag ${days})` : `vor ${days} Tagen`}
            </Label>
        </div>
    );
};

const AccountCell = ({account}) => {
    if (!account) {
        return <span style={{color: '#999'}}>Kein Konto gefunden</span>;
    }
    return (
        <div>
            <div>{account.nickname}</div>
            <div style={{fontSize: 12, color: '#777'}}>{account.email}</div>
            {account.confirmed
                ? <Label size="tiny" basic color="green">angemeldet eingereicht</Label>
                : <Label size="tiny" basic color="orange">nur per E-Mail zugeordnet</Label>}
            {account.has_active_subscription
                ? <Label size="tiny" basic>Abo aktiv</Label>
                : <Label size="tiny" basic>kein aktives Abo</Label>}
            <AboDate value={account.subscription_created_at} />
        </div>
    );
};

const WithdrawalAdmin = () => {
    const authToken = useSelector(getAuthToken);
    const clientAud = useSelector(getClientAud);

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [statusFilter, setStatusFilter] = useState('pending');
    const [confirming, setConfirming] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [accountQuery, setAccountQuery] = useState('');
    const [accountResults, setAccountResults] = useState([]);
    const [pickedAccount, setPickedAccount] = useState(null);
    const [saving, setSaving] = useState(false);

    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const authHeaders = useCallback((extra = {}) => (
        authToken
            ? {Authorization: `Bearer ${authToken}`, JWT_AUD: clientAud, ...extra}
            : {...extra}
    ), [authToken, clientAud]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const query = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
            const res = await fetch(`/api/v1/withdrawal_requests${query}`, {
                headers: authHeaders(),
                credentials: 'include',
            });
            if (!res.ok) throw new Error(`Fehler beim Laden (${res.status})`);
            const json = await res.json();
            if (mounted.current) setRequests(json.withdrawal_requests || []);
        } catch (e) {
            if (mounted.current) setError(e.message);
        } finally {
            if (mounted.current) setLoading(false);
        }
    }, [authHeaders, statusFilter]);

    useEffect(() => { load(); }, [load]);

    // Debounced so typing a name does not fire a request per keystroke.
    useEffect(() => {
        if (!creating) return undefined;
        const q = accountQuery.trim();
        if (!q) { setAccountResults([]); return undefined; }
        const timer = setTimeout(async () => {
            try {
                const res = await fetch(
                    `/api/v1/withdrawal_requests/account_search?q=${encodeURIComponent(q)}`,
                    {headers: authHeaders(), credentials: 'include'}
                );
                if (!res.ok) return;
                const json = await res.json();
                if (mounted.current) setAccountResults(json.accounts || []);
            } catch (_) { /* search is a convenience, never block the form */ }
        }, 300);
        return () => clearTimeout(timer);
    }, [accountQuery, creating, authHeaders]);

    function openCreate() {
        setForm(EMPTY_FORM);
        setPickedAccount(null);
        setAccountQuery('');
        setAccountResults([]);
        setError(null);
        setCreating(true);
    }

    function pickAccount(account) {
        setPickedAccount(account);
        setAccountResults([]);
        setAccountQuery('');
        // Prefill from the account, but never overwrite what the admin typed.
        setForm(prev => {
            const parts = (account.nickname || '').trim().split(/\s+/);
            return {
                ...prev,
                email: prev.email || account.email || '',
                first_name: prev.first_name || parts[0] || '',
                last_name: prev.last_name || parts.slice(1).join(' ') || '',
            };
        });
    }

    async function submitCreate() {
        setSaving(true);
        setError(null);
        try {
            const res = await fetch('/api/v1/withdrawal_requests', {
                method: 'POST',
                headers: authHeaders({'Content-Type': 'application/json'}),
                credentials: 'include',
                body: JSON.stringify({
                    ...form,
                    user_slug: pickedAccount ? pickedAccount.slug : undefined,
                }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                const detail = json?.error?.detail;
                throw new Error(typeof detail === 'object'
                    ? Object.values(detail).flat().join(' ')
                    : (detail || json.message || `Fehler (${res.status})`));
            }
            setCreating(false);
            setNotice('Widerruf erfasst. Er steht jetzt als „Offen“ in der Liste.');
            if (statusFilter && statusFilter !== 'pending') setStatusFilter('pending');
            else await load();
        } catch (e) {
            if (mounted.current) setError(e.message);
        } finally {
            if (mounted.current) setSaving(false);
        }
    }

    async function act(request, action) {
        setBusyId(request.id);
        setError(null);
        setNotice(null);
        try {
            // An unconfirmed match was only guessed from the typed email, so send
            // the chosen account explicitly rather than letting the server assume.
            const body = (action === 'terminate' && request.account && !request.account.confirmed)
                ? JSON.stringify({user_slug: request.account.slug})
                : null;
            const res = await fetch(`/api/v1/withdrawal_requests/${request.id}/${action}`, {
                method: 'POST',
                headers: authHeaders({'Content-Type': 'application/json'}),
                credentials: 'include',
                body,
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(json.message || json.error || `Fehler (${res.status})`);
            }
            if (action === 'terminate') {
                const count = json.terminated_subscriptions || 0;
                setNotice(count > 0
                    ? `Zugang beendet. ${count} Abo(s) sofort beendet. `
                      + 'Erstattung bitte noch manuell im Stripe-/PayPal-Dashboard vornehmen.'
                    : 'Widerruf als bearbeitet markiert. Es gab kein laufendes Abo.');
            } else {
                setNotice('Widerruf abgelehnt. Das Abo bleibt unverändert.');
            }
            await load();
        } catch (e) {
            if (mounted.current) setError(e.message);
        } finally {
            if (mounted.current) setBusyId(null);
        }
    }

    return (
        <div>
            <Header as="h2">Widerrufe</Header>

            <p style={{color: '#666'}}>
                „Abo sofort beenden“ entfernt den Zugang unmittelbar — nicht erst zum Ende der
                bezahlten Laufzeit. Die Erstattung erfolgt weiterhin manuell im Stripe- bzw.
                PayPal-Dashboard.
            </p>

            <div style={{marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center'}}>
                <Dropdown selection compact
                    options={STATUS_FILTERS}
                    value={statusFilter}
                    onChange={(e, d) => setStatusFilter(d.value)}
                    data-cy="widerruf-admin-filter"
                />
                <Button primary size="small" onClick={openCreate}
                    data-cy="widerruf-admin-new"
                >
                    <Icon name="plus" /> Widerruf erfassen
                </Button>
            </div>

            {error && <Message negative onDismiss={() => setError(null)}>{error}</Message>}
            {notice && <Message positive onDismiss={() => setNotice(null)}>{notice}</Message>}

            {loading ? <Loader active inline="centered" /> : (
                <div style={{overflowX: 'auto', width: '100%'}}>
                <Table celled striped compact data-cy="widerruf-admin-table">
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Eingegangen</Table.HeaderCell>
                            <Table.HeaderCell>Name</Table.HeaderCell>
                            <Table.HeaderCell>E-Mail / Bestellnr.</Table.HeaderCell>
                            <Table.HeaderCell>Art</Table.HeaderCell>
                            <Table.HeaderCell>Konto</Table.HeaderCell>
                            <Table.HeaderCell>Status</Table.HeaderCell>
                            <Table.HeaderCell>Aktion</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {requests.length === 0 && (
                            <Table.Row>
                                <Table.Cell colSpan="7" textAlign="center" style={{color: '#999'}}>
                                    Keine Widerrufe in dieser Ansicht.
                                </Table.Cell>
                            </Table.Row>
                        )}
                        {requests.map(r => (
                            <Table.Row key={r.id}>
                                <Table.Cell>{formatDate(r.created_at)}</Table.Cell>
                                <Table.Cell>{r.full_name}</Table.Cell>
                                <Table.Cell>
                                    <div>{r.email}</div>
                                    <div style={{fontSize: 12, color: '#777'}}>{r.order_id}</div>
                                </Table.Cell>
                                <Table.Cell>{r.withdrawal_type_label}</Table.Cell>
                                <Table.Cell><AccountCell account={r.account} /></Table.Cell>
                                <Table.Cell>
                                    <StatusLabel status={r.status} />
                                    {r.processed_by && (
                                        <div style={{fontSize: 12, color: '#777', marginTop: 4}}>
                                            {r.processed_by}<br />{formatDate(r.processed_at)}
                                        </div>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    {r.status === 'pending' ? (
                                        <Button.Group size="mini" vertical>
                                            {/* Only offered when there is actually a running Abo
                                                to end. A "beenden" button on a member whose Abo
                                                has already lapsed reads as a lie, even though the
                                                action itself is harmless. */}
                                            {r.account && r.account.has_active_subscription && (
                                                <Button negative
                                                    disabled={busyId === r.id}
                                                    loading={busyId === r.id}
                                                    onClick={() => setConfirming(r)}
                                                    data-cy="widerruf-admin-terminate"
                                                >
                                                    <Icon name="ban" /> Abo sofort beenden
                                                </Button>
                                            )}
                                            <Button basic
                                                disabled={busyId === r.id}
                                                onClick={() => act(r, 'reject')}
                                                data-cy="widerruf-admin-reject"
                                            >
                                                Ablehnen
                                            </Button>
                                        </Button.Group>
                                    ) : <span style={{color: '#999'}}>—</span>}
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
                </div>
            )}

            <Modal open={creating} size="small" onClose={() => setCreating(false)}>
                <Modal.Header>Widerruf erfassen</Modal.Header>
                <Modal.Content>
                    <p style={{color: '#666'}}>
                        Für Widerrufe, die nicht über das Formular kommen — z. B. per E-Mail,
                        Telefon oder Post.
                    </p>

                    <Form>
                        <Form.Field>
                            <label>Konto suchen (Name oder E-Mail)</label>
                            {pickedAccount ? (
                                <div style={{
                                    padding: '8px 10px', border: '1px solid #d4d4d5', borderRadius: 4,
                                }}>
                                    <strong>{pickedAccount.nickname}</strong>{' '}
                                    <span style={{color: '#777'}}>{pickedAccount.email}</span>{' '}
                                    {pickedAccount.has_active_subscription
                                        ? <Label size="tiny" color="green">Abo aktiv</Label>
                                        : <Label size="tiny">kein aktives Abo</Label>}
                                    <Button basic size="mini" floated="right"
                                        onClick={() => setPickedAccount(null)}
                                    >
                                        Ändern
                                    </Button>
                                </div>
                            ) : (<>
                                <Input fluid icon="search"
                                    placeholder="z. B. Anna oder anna@..."
                                    value={accountQuery}
                                    onChange={(e) => setAccountQuery(e.target.value)}
                                    data-cy="widerruf-admin-account-search"
                                />
                                {accountResults.length > 0 && (
                                    <List selection divided style={{
                                        maxHeight: 180, overflowY: 'auto',
                                        border: '1px solid #eee', marginTop: 6,
                                    }}>
                                        {accountResults.map(a => (
                                            <List.Item key={a.slug} onClick={() => pickAccount(a)}>
                                                <List.Content>
                                                    <List.Header>{a.nickname}</List.Header>
                                                    <div style={{fontSize: 12, color: '#777'}}>
                                                        {a.email}
                                                        {a.has_active_subscription
                                                            ? ' · Abo aktiv' : ' · kein aktives Abo'}
                                                    </div>
                                                </List.Content>
                                            </List.Item>
                                        ))}
                                    </List>
                                )}
                                <div style={{fontSize: 12, color: '#999', marginTop: 4}}>
                                    Ohne Konto kann der Widerruf erfasst, aber das Abo noch nicht
                                    beendet werden.
                                </div>
                            </>)}
                        </Form.Field>

                        <Form.Group widths="equal">
                            <Form.Input label="Vorname" required
                                value={form.first_name}
                                onChange={(e) => setForm({...form, first_name: e.target.value})}
                            />
                            <Form.Input label="Nachname" required
                                value={form.last_name}
                                onChange={(e) => setForm({...form, last_name: e.target.value})}
                            />
                        </Form.Group>
                        <Form.Input label="E-Mail" required type="email"
                            value={form.email}
                            onChange={(e) => setForm({...form, email: e.target.value})}
                        />
                        <Form.Input label="Bestellnummer / Referenz" required
                            value={form.order_id}
                            onChange={(e) => setForm({...form, order_id: e.target.value})}
                        />
                        <Form.Select label="Art des Widerrufsrechts" required
                            options={WITHDRAWAL_TYPE_OPTIONS}
                            value={form.withdrawal_type}
                            onChange={(e, d) => setForm({...form, withdrawal_type: d.value})}
                            data-cy="widerruf-admin-new-type"
                        />
                        <Form.TextArea label="Notiz (optional)" rows={3}
                            placeholder="z. B. Widerruf kam per E-Mail am 04.08."
                            value={form.note}
                            onChange={(e) => setForm({...form, note: e.target.value})}
                        />
                    </Form>

                    <Message info size="small">
                        Das Erfassen beendet noch kein Abo. Der Eintrag erscheint als „Offen“ und
                        muss anschließend in der Liste bearbeitet werden.
                    </Message>
                </Modal.Content>
                <Modal.Actions>
                    <Button basic onClick={() => setCreating(false)}>Abbrechen</Button>
                    <Button primary loading={saving}
                        disabled={saving || !form.withdrawal_type}
                        onClick={submitCreate}
                        data-cy="widerruf-admin-new-save"
                    >
                        Erfassen
                    </Button>
                </Modal.Actions>
            </Modal>

            <Modal open={confirming !== null} size="small"
                onClose={() => setConfirming(null)}
            >
                <Modal.Header>Abo sofort beenden?</Modal.Header>
                <Modal.Content>
                    {confirming && (<>
                        <p>
                            Der Zugang von <strong>{confirming.account?.nickname}</strong>{' '}
                            ({confirming.account?.email}) wird <strong>sofort</strong> entfernt —
                            nicht erst zum Ende der bezahlten Laufzeit.
                        </p>
                        {confirming.account && !confirming.account.confirmed && (
                            <Message warning>
                                Dieses Konto wurde nur über die eingegebene E-Mail-Adresse
                                zugeordnet, nicht beim Einreichen bestätigt. Bitte prüfen, ob es
                                wirklich das richtige Konto ist.
                            </Message>
                        )}
                        <Message info>
                            Die Erstattung wird hierdurch <strong>nicht</strong> ausgelöst — diese
                            bitte manuell im Stripe- bzw. PayPal-Dashboard vornehmen.
                        </Message>
                    </>)}
                </Modal.Content>
                <Modal.Actions>
                    <Button basic onClick={() => setConfirming(null)}>Abbrechen</Button>
                    <Button negative
                        onClick={() => {
                            const target = confirming;
                            setConfirming(null);
                            act(target, 'terminate');
                        }}
                        data-cy="widerruf-admin-terminate-confirm"
                    >
                        <Icon name="ban" /> Abo sofort beenden
                    </Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};

export default WithdrawalAdmin;
